import axios, { isAxiosError } from 'axios';
import { createMutation, createQuery } from 'react-query-kit';

import { restUrl } from '@/services/utils';

interface CurrentOperationPayload {
    updatedAt?: number | string | null;
}

const useCurrentOperationTexts = createQuery<string[], void, Error>({
    queryKey: ['currentOperations', 'textsOnly'],
    fetcher: async () => {
        // Same request the Angular page effectively made. It sent `currentOperations`,
        // which CurrentOperationResource ignores, so the server only filters on updatedAt.
        // Sending `operationTypes` needs a Datastore composite index that is not deployed.
        const response = await axios.get<string[]>(restUrl('currentOperations'), {
            params: {
                textsOnly: true,
            },
        });

        return response.data ?? [];
    },
});

const useRevalidateItems = createMutation<void, { onlyDirty?: boolean }>({
    mutationFn: async ({ onlyDirty }) => {
        await axios.get(restUrl('revalidateItems'), {
            params: onlyDirty ? { onlyDirty: true } : undefined,
        });
    },
});

const useRecalculateStatistics = createMutation<void, { force?: boolean }>({
    mutationFn: async ({ force }) => {
        await axios.get(restUrl('recalculateStatistics'), {
            params: force ? { force: true } : undefined,
        });
    },
});

const useReindexItems = createMutation<void, void>({
    mutationFn: async () => {
        await axios.post(restUrl('reindexItems'));
    },
});

const useDeleteOldUnfinishedExports = createMutation<void, { before: string }>({
    mutationFn: async ({ before }) => {
        await axios.delete(restUrl('items/exports'), {
            params: { before },
        });
    },
});

const useRecalculateOperation = createMutation<CurrentOperationPayload | null | '', void>({
    mutationFn: async () => {
        const response = await axios.get<CurrentOperationPayload | null | ''>(
            restUrl('currentOperations/RECALCULATE_STATISTICS'),
        );
        return response.data;
    },
});

function unfinishedExportsCutoff(now = new Date()): string {
    const presentDate = new Date(now.getTime());
    presentDate.setHours(presentDate.getHours() - 24);
    return presentDate.toISOString();
}

function readUpdatedAt(
    operation: CurrentOperationPayload | null | undefined | '',
): number | string | null {
    if (operation == null || operation === '' || typeof operation !== 'object') {
        return null;
    }

    if (Object.keys(operation).length === 0) {
        return null;
    }

    const updatedAt = operation.updatedAt;
    if (updatedAt == null || updatedAt === '') {
        return null;
    }

    return updatedAt;
}

function isPreconditionFailed(error: unknown): boolean {
    return isAxiosError(error) && error.response?.status === 412;
}

export {
    isPreconditionFailed,
    readUpdatedAt,
    unfinishedExportsCutoff,
    useCurrentOperationTexts,
    useDeleteOldUnfinishedExports,
    useRecalculateOperation,
    useRecalculateStatistics,
    useReindexItems,
    useRevalidateItems,
};
